import { useState } from 'react';
import { Code, Eye, Copy, Check, FileCode, FileText, Palette } from 'lucide-react';
import { cn } from '../utils/api';

interface ArtifactRendererProps {
  type: 'code' | 'html' | 'react' | 'mermaid' | 'svg' | 'markdown';
  title: string;
  content: string;
  language?: string;
}

export default function ArtifactRenderer({ type, title, content }: ArtifactRendererProps) {
  const [activeTab, setActiveTab] = useState<'code' | 'preview'>('code');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getIcon = () => {
    switch (type) {
      case 'code': return <FileCode className="w-4 h-4" />;
      case 'html': return <FileText className="w-4 h-4" />;
      case 'react': return <Code className="w-4 h-4" />;
      case 'mermaid': return <Palette className="w-4 h-4" />;
      default: return <FileCode className="w-4 h-4" />;
    }
  };

  const canPreview = ['html', 'react', 'mermaid', 'svg'].includes(type);

  const generatePreviewHTML = () => {
    switch (type) {
      case 'html':
        return content;
      case 'react':
        return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <script src="https://unpkg.com/react@18/umd/react.development.js"></script>
  <script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  <style>body { margin: 0; padding: 20px; font-family: system-ui; }</style>
</head>
<body>
  <div id="root"></div>
  <script type="text/babel">
    ${content}
    ReactDOM.render(<App />, document.getElementById('root'));
  </script>
</body>
</html>`;
      case 'mermaid':
        return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <script src="https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js"></script>
</head>
<body>
  <div class="mermaid">${content}</div>
  <script>mermaid.initialize({ startOnLoad: true });</script>
</body>
</html>`;
      case 'svg':
        return content;
      default:
        return content;
    }
  };

  return (
    <div className="artifact-container bg-legion-card border border-legion-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-legion-dark border-b border-legion-border">
        <div className="flex items-center gap-2">
          {getIcon()}
          <span className="font-medium text-sm">{title}</span>
          <span className="text-xs text-gray-500 px-2 py-0.5 bg-white/5 rounded">
            {type}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {canPreview && (
            <>
              <button
                onClick={() => setActiveTab('code')}
                className={cn(
                  "px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1.5",
                  activeTab === 'code'
                    ? "bg-indigo-600 text-white"
                    : "hover:bg-white/10 text-gray-400"
                )}
              >
                <Code className="w-3.5 h-3.5" />
                Code
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                className={cn(
                  "px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1.5",
                  activeTab === 'preview'
                    ? "bg-indigo-600 text-white"
                    : "hover:bg-white/10 text-gray-400"
                )}
              >
                <Eye className="w-3.5 h-3.5" />
                Preview
              </button>
            </>
          )}
          <button
            onClick={handleCopy}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
            title="Copy code"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        {activeTab === 'code' ? (
          <div className="max-h-[400px] overflow-auto bg-[#1e1e1e] p-4">
            <pre className="text-sm font-mono text-gray-300">
              <code>{content}</code>
            </pre>
          </div>
        ) : (
          <iframe
            srcDoc={generatePreviewHTML()}
            className="w-full h-[400px] bg-white"
            sandbox="allow-scripts allow-same-origin"
            title={title}
          />
        )}
      </div>
    </div>
  );
}
